import i18n from 'config/i18n';
import dayjs, { Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import * as yup from 'yup';

import { getLocale } from 'utils/date';

dayjs.extend(minMax);

const copyPeriodSchema = () => {
  const t = i18n.getFixedT(getLocale(), 'common');

  const testSourceAndTargetDate = (
    v: Dayjs | undefined,
    ctx: yup.TestContext,
    path: 'startDate' | 'endDate'
  ) => {
    const { sourceStartDate, sourceEndDate } = ctx.parent;

    if (!v || !dayjs(v).isValid())
      return ctx.createError({
        message: t('invalidDate'),
        path
      });

    // Check if the target date is within the same period as the source date
    if (
      v.valueOf() >= sourceStartDate.valueOf() &&
      v.valueOf() <= sourceEndDate.valueOf()
    ) {
      return ctx.createError({
        message: t('validationTargetAndSourceSamePeriod'),
        path
      });
    }

    return true;
  };

  return yup.object({
    hasProject: yup.boolean().notRequired(),
    organizationalUnit: yup.mixed().when(['hasProject'], {
      is: (hasProject?: boolean) => {
        return !hasProject;
      },
      then: yup
        .mixed()
        .required(t('validationFieldRequired', { name: t('planUnit') }))
    }),
    sourceStartDate: yup.mixed<Dayjs>(),
    sourceEndDate: yup.mixed<Dayjs>(),
    startDate: yup
      .mixed<Dayjs>()
      .test('startDate-valid', (v, ctx) =>
        testSourceAndTargetDate(v, ctx, 'startDate')
      )
      .required(t('validationFieldRequired', { name: t('startDate') })),
    endDate: yup
      .mixed<Dayjs>()
      .test('endDate-valid', (v, ctx) =>
        testSourceAndTargetDate(v, ctx, 'endDate')
      )
      .test(
        'is-after-destination-start',
        t('validationStartAndEndDateDifference'),
        function (value) {
          const { startDate }: { startDate: Dayjs } = this.parent;
          return startDate.isBefore(value);
        }
      )
      .required(t('validationFieldRequired', { name: t('endDate') })),
    includeEmployee: yup
      .boolean()
      .notRequired()
      .typeError(
        t('validation.invalid', {
          name: t('activityPlanning.includeEmployeeInCopy')
        })
      ),
    copyWeekInterval: yup
      .number()
      .notRequired()
      .default(1)
      .min(1, t('validation.greaterThan', { field: 1 }))
      .max(12, t('validation.lessThan', { field: 12 }))
      .typeError(
        t('validation.invalid', {
          name: t('activityPlanning.copyWeekInterval')
        })
      )
  });
};

export default copyPeriodSchema;
